import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { Hidden, MenuItem, Pagination as PaginationMaterialUI } from "@mui/material";

import { RafterDownIcon } from "@/assets/icons";
import { PageParams } from "@/shared/domain";

import { PageSizeLabel, StyledPageSize, StyledPagination, StyledPaginationItem, StyledSelect } from "./styled";

type PaginationProps = {
    page: PageParams;
    disabled?: boolean;
    pageCount: number;
    tableMode: boolean;
    onPageChange: (value: number) => void;
    onPageSizeChange: (value: number) => void;
};

const Pagination: FC<PaginationProps> = ({
    disabled = false,
    page,
    pageCount,
    onPageChange,
    onPageSizeChange,
    tableMode,
}) => {
    const pageElement = document.querySelector("body");
    const pageSizeOptions = [...(tableMode ? [] : [5]), 10, 25, 50, 100];
    const { pageSize, pageNumber } = page;
    const { t } = useTranslation();

    const handlePageChange = (_, value) => {
        onPageChange(value);
        pageElement.scrollIntoView({ block: "start", behavior: "smooth" });
    };

    if (pageCount === 0) {
        return null;
    }

    return (
        <StyledPagination className="pagination">
            <PaginationMaterialUI
                classes={{ ul: "pagination-ul" }}
                renderItem={(item) => {
                    const paginationItemTestId = `pagination-item-${item.type === "page" ? item.page : item.type}`;
                    return (
                        <StyledPaginationItem
                            {...item}
                            data-testid={paginationItemTestId}
                            classes={{ page: "page", icon: "icon", selected: "selected" }}
                        />
                    );
                }}
                disabled={disabled}
                count={pageCount || 0}
                onChange={handlePageChange}
                page={pageNumber}
            />
            <Hidden smDown>
                <StyledPageSize>
                    <PageSizeLabel>{t("Pagination.RowsPerPage")}</PageSizeLabel>
                    <StyledSelect
                        classes={{ select: "select" }}
                        value={pageSize}
                        variant="standard"
                        onChange={(e: any) => onPageSizeChange(e.target.value)}
                        inputProps={{
                            "data-testid": "pagination-select-page_size",
                        }}
                        IconComponent={RafterDownIcon}
                    >
                        {pageSizeOptions.map((pageSizeOption) => (
                            <MenuItem
                                data-testid={`option-pageSize-${pageSizeOption}`}
                                key={`option-pageSize-${pageSizeOption}`}
                                value={pageSizeOption}
                            >
                                {pageSizeOption}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </StyledPageSize>
            </Hidden>
        </StyledPagination>
    );
};

export default Pagination;
